import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import SignatureCanvas from 'react-signature-canvas';
import { send as sendOrQueue } from '../lib/offline';

// Never call the API with "Bearer null": if the sign-in vanished, say so and go to login.
const requireToken = () => {
  const t = localStorage.getItem('token');
  if (!t || t === 'null' || t === 'undefined') {
    alert('Your sign-in has expired. Please sign in again.');
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  return t;
};
import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ClockIcon,
  NoSymbolIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const DailyPSIForm = () => {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Managers/admins can edit any PSI anytime. The foreman who created a PSI can keep
  // editing it (add workers/tasks/hazards) throughout the SAME day it was created.
  const INSPECTION_EDIT_ROLES = ['super_admin', 'admin', 'shop_foreman', 'fabrication_manager'];
  
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    work_order_number: '',
    job_site: '',
    work_description: '',
    crew_members: [''],
    weather_conditions: '',
    temperature: ''
  });
  
  // Risk assessment data
  const [hazardAssessments, setHazardAssessments] = useState([]);
  const [jobSteps, setJobSteps] = useState([]);
  const [workerSignatures, setWorkerSignatures] = useState([]);
  
  // Signature canvas
  const signatureRef = useRef(null);
  const [currentSignature, setCurrentSignature] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  
  // Crew clock-in popup state
  const [showCrewClockin, setShowCrewClockin] = useState(false);
  const [crewClockTime, setCrewClockTime] = useState('07:00');
  const [crewShift, setCrewShift] = useState('days');
  const [crewClockLoading, setCrewClockLoading] = useState(false);
  const [crewClockResults, setCrewClockResults] = useState(null);
  const [psiQueued, setPsiQueued] = useState(false);
  const [savedWO, setSavedWO] = useState('');
  
  // Auto-save state
  const [hasDraft, setHasDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const draftKey = 'psi_draft_v1';

  // Edit gate: managers/admins can always edit; the creator can edit their own PSI
  // only on the same calendar day it was created.
  const isManager = INSPECTION_EDIT_ROLES.includes(user?.role);
  const isCreator = !!(inspection && user?.id && inspection.inspector_id === user.id);
  const todayET = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  const isSameDay = !inspection || (inspection.inspection_date || '') === todayET;
  const locked = !!inspectionId && !(isManager || (isCreator && isSameDay));

  // Format an ISO/DB timestamp for the edit-history strip (Eastern time).
  const fmtHistoryTs = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        hour12: true, timeZone: 'America/Toronto'
      });
    } catch (e) { return ''; }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const riskLevels = [
    { value: 1, label: 'Very Low', color: 'text-green-600' },
    { value: 2, label: 'Low', color: 'text-green-500' },
    { value: 3, label: 'Medium', color: 'text-amber-600' },
    { value: 4, label: 'High', color: 'text-orange-500' },
    { value: 5, label: 'Very High', color: 'text-red-500' }
  ];

  useEffect(() => {
    console.log('DailyPSIForm mounted - inspectionId:', inspectionId);
    if (inspectionId) {
      console.log('Loading existing inspection...');
      loadExistingInspection();
    } else {
      console.log('Initializing new inspection...');
      initializeNewInspection();
    }
  }, [inspectionId]);
  
  // Log when formData changes to verify loading
  useEffect(() => {
    console.log('FormData updated:', formData);
  }, [formData]);
  
  // Log when inspection state changes
  useEffect(() => {
    console.log('Inspection state updated:', inspection);
  }, [inspection]);

  // Track previous crew member names to detect changes
  const [previousCrewMembers, setPreviousCrewMembers] = useState([]);

  // "Same site, same job as last time?" — the foreman's recent PSIs offered as a starting point for a new one
  const [recentPSIs, setRecentPSIs] = useState([]);
  const [templatePick, setTemplatePick] = useState('');
  const [templateUsed, setTemplateUsed] = useState(null);
  const [templateDismissed, setTemplateDismissed] = useState(false);

  const fmtPsiDay = (d) => {
    if (!d) return '';
    try { return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); } catch { return d; }
  };
  const psiLabel = (p) => {
    const crew = (p.crew_members || []).filter((c) => c && c.trim()).length;
    const tasks = (p.job_steps || []).filter((s) => s.description && s.description.trim()).length;
    return `${fmtPsiDay(p.inspection_date)} · WO ${p.work_order_number || '—'}${p.job_site ? ` · ${p.job_site}` : ''} · ${crew} crew · ${tasks} task${tasks === 1 ? '' : 's'}`;
  };

  const loadRecentPSIs = async () => {
    try {
      const token = requireToken();
      const r = await fetch(`${backendUrl}/api/inspections/my-inspections?limit=25`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const data = await r.json();
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 21);
      const cut = cutoff.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
      const list = (data.inspections || [])
        .filter((p) => p.inspection_type === 'daily_psi' && (p.inspection_date || '') >= cut && (p.inspection_date || '') < todayET)
        .sort((a, b) => (b.inspection_date || '').localeCompare(a.inspection_date || '') || (b.created_at || '').localeCompare(a.created_at || ''))
        .slice(0, 6);
      setRecentPSIs(list);
      if (list.length) setTemplatePick(list[0].inspection_id);
    } catch (e) { /* offer nothing */ }
  };

  // Copy yesterday's PSI: same WO / site / job description, crew, hazards and tasks. Today's checklist, weather and
  // signatures start fresh — everyone on site signs again.
  const applyTemplate = (p) => {
    if (!p) return;
    const names = (p.crew_members || []).map((c) => (c || '').trim()).filter(Boolean);
    setFormData({
      work_order_number: p.work_order_number || '',
      job_site: p.job_site || '',
      work_description: p.work_description || '',
      crew_members: names.length ? names : [''],
      weather_conditions: '',
      temperature: '',
    });
    if (p.hazard_assessments && p.hazard_assessments.length) {
      setHazardAssessments(p.hazard_assessments.map((h) => ({ ...h })));
    }
    if (p.job_steps && p.job_steps.length) {
      setJobSteps(p.job_steps.map((s, i) => ({ ...s, step_number: i + 1 })));
    }
    // Copied crew get an (unsigned) signature line right away — no need to retype names to make the sign button appear
    setWorkerSignatures(names.map((n) => ({ name: n, position: 'Worker', signature_data: null, timestamp: new Date().toISOString() })));
    setPreviousCrewMembers(names);
    setInspection((prev) => ({ ...(prev || {}), template_from_inspection_id: p.inspection_id, template_from_date: p.inspection_date }));
    setTemplateUsed(p);
  };

  // Auto-save draft every 30 seconds (only for new inspections)
  useEffect(() => {
    if (inspectionId || loading || !inspection) return;
    
    const saveDraft = () => {
      try {
        const draft = {
          formData,
          inspection,
          hazardAssessments,
          jobSteps,
          workerSignatures: workerSignatures.map(s => ({ ...s, signature_data: s.signature_data || null })),
          savedAt: new Date().toISOString()
        };
        localStorage.setItem(draftKey, JSON.stringify(draft));
        setLastSaved(new Date());
        setHasDraft(true);
      } catch (e) {
        console.warn('Auto-save failed:', e);
      }
    };
    
    const interval = setInterval(saveDraft, 30000);
    
    // Also save on page hide (phone call, app switch, tab switch)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveDraft();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [inspectionId, loading, inspection, formData, hazardAssessments, jobSteps, workerSignatures]);

  const clearDraft = () => {
    localStorage.removeItem(draftKey);
    setHasDraft(false);
    setLastSaved(null);
  };

  const restoreDraft = (draft) => {
    if (draft.formData) setFormData(draft.formData);
    if (draft.hazardAssessments) setHazardAssessments(draft.hazardAssessments);
    if (draft.jobSteps) setJobSteps(draft.jobSteps);
    const names = ((draft.formData || {}).crew_members || []).map((c) => (c || '').trim()).filter(Boolean);
    const sigs = [...(draft.workerSignatures || [])];
    names.forEach((n) => { if (!sigs.some((s) => (s.name || '').trim().toLowerCase() === n.toLowerCase())) sigs.push({ name: n, position: 'Worker', signature_data: null, timestamp: new Date().toISOString() }); });
    setWorkerSignatures(sigs);
    setPreviousCrewMembers(names);
    if (draft.inspection) setInspection(draft.inspection);
    setHasDraft(true);
    setLastSaved(new Date(draft.savedAt));
  };

  const initializeNewInspection = async () => {
    try {
      // Check for saved draft first
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          // Only restore if draft is less than 24 hours old
          const draftAge = Date.now() - new Date(draft.savedAt).getTime();
          if (draftAge < 24 * 60 * 60 * 1000 && draft.inspection) {
            restoreDraft(draft);
            setLoading(false);
            return;
          } else {
            localStorage.removeItem(draftKey);
          }
        } catch (e) {
          localStorage.removeItem(draftKey);
        }
      }
      
      const token = requireToken();
      let templateItems = null;
      try {
        const response = await fetch(`${backendUrl}/api/inspections/templates/daily_psi`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          templateItems = (await response.json()).template_items;
          localStorage.setItem('psi_template_cache', JSON.stringify(templateItems));
        }
      } catch (netErr) {
        // No signal — use the checklist from the last time this phone loaded it
        const cached = localStorage.getItem('psi_template_cache');
        if (cached) templateItems = JSON.parse(cached);
        else throw netErr;
      }

      if (templateItems) {
        const data = { template_items: templateItems };
        
        // Initialize with streamlined checklist
        setInspection({
          inspection_type: 'daily_psi',
          inspection_items: data.template_items.map((item, index) => ({
            id: `item_${index}`,
            description: item.description,
            status: null,
            notes: '',
            photo_urls: []
          })),
          overall_status: null,
          deficiencies_noted: false
        });

        // Initialize default hazard assessments
        const defaultHazards = [
          { hazard_type: "Fall Hazards", description: "", risk_level: 1, control_measures: "" },
          { hazard_type: "Struck-By", description: "", risk_level: 1, control_measures: "" },
          { hazard_type: "Electrical", description: "", risk_level: 1, control_measures: "" },
          { hazard_type: "Chemical/Environmental", description: "", risk_level: 1, control_measures: "" }
        ];
        setHazardAssessments(defaultHazards);

        // Initialize first job step
        setJobSteps([{
          step_number: 1,
          description: "",
          hazards: "",
          risk_level: 1,
          control_measures: ""
        }]);
        loadRecentPSIs();
      }
    } catch (error) {
      console.error('Failed to load template:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingInspection = async () => {
    try {
      console.log('Loading existing inspection:', inspectionId);
      const token = requireToken();
      const response = await fetch(`${backendUrl}/api/inspections/${inspectionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const inspectionData = await response.json();
        console.log('Loaded inspection data:', inspectionData);
        
        // Load all form data
        setInspection(inspectionData);
        setFormData({
          work_order_number: inspectionData.work_order_number || '',
          job_site: inspectionData.job_site || '',
          work_description: inspectionData.work_description || '',
          crew_members: inspectionData.crew_members && inspectionData.crew_members.length > 0 ? inspectionData.crew_members : [''],
          weather_conditions: inspectionData.weather_conditions || '',
          temperature: inspectionData.temperature || ''
        });
        
        // Load hazard assessments with defaults
        if (inspectionData.hazard_assessments && inspectionData.hazard_assessments.length > 0) {
          setHazardAssessments(inspectionData.hazard_assessments);
        } else {
          // Initialize with defaults if none exist
          setHazardAssessments([
            { hazard_type: "Fall Hazards", description: "", risk_level: 1, control_measures: "" },
            { hazard_type: "Struck-By", description: "", risk_level: 1, control_measures: "" },
            { hazard_type: "Electrical", description: "", risk_level: 1, control_measures: "" },
            { hazard_type: "Chemical/Environmental", description: "", risk_level: 1, control_measures: "" }
          ]);
        }
        
        // Load job steps with defaults
        if (inspectionData.job_steps && inspectionData.job_steps.length > 0) {
          setJobSteps(inspectionData.job_steps);
        } else {
          // Initialize with one empty step if none exist
          setJobSteps([{
            step_number: 1,
            description: "",
            hazards: "",
            risk_level: 1,
            control_measures: ""
          }]);
        }
        
        // Load worker signatures
        if (inspectionData.worker_signatures && inspectionData.worker_signatures.length > 0) {
          setWorkerSignatures(inspectionData.worker_signatures);
        }
        
        console.log('Successfully loaded inspection data');
      } else {
        const errorText = await response.text();
        console.error('Failed to load inspection:', response.status, errorText);
        throw new Error(`Failed to load inspection: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to load existing inspection:', error);
      alert(`Failed to load inspection data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallRisk = () => {
    const hazardRisk = hazardAssessments.reduce((sum, hazard) => sum + hazard.risk_level, 0);
    const jobStepRisk = jobSteps.reduce((sum, step) => sum + step.risk_level, 0);
    return hazardRisk + jobStepRisk;
  };

  const getRiskColor = (risk) => {
    if (risk <= 8) return 'text-green-600 bg-green-100';
    if (risk <= 16) return 'text-yellow-600 bg-yellow-100';
    if (risk <= 24) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const addHazardAssessment = () => {
    setHazardAssessments([...hazardAssessments, {
      hazard_type: "Other",
      description: "",
      risk_level: 1,
      control_measures: ""
    }]);
  };

  const removeHazardAssessment = (index) => {
    setHazardAssessments(hazardAssessments.filter((_, i) => i !== index));
  };

  const updateHazardAssessment = (index, field, value) => {
    const updated = [...hazardAssessments];
    updated[index][field] = value;
    setHazardAssessments(updated);
  };

  const addJobStep = () => {
    setJobSteps([...jobSteps, {
      step_number: jobSteps.length + 1,
      description: "",
      hazards: "",
      risk_level: 1,
      control_measures: ""
    }]);
  };

  const removeJobStep = (index) => {
    const updated = jobSteps.filter((_, i) => i !== index);
    // Renumber steps
    updated.forEach((step, i) => step.step_number = i + 1);
    setJobSteps(updated);
  };

  const updateJobStep = (index, field, value) => {
    const updated = [...jobSteps];
    updated[index][field] = value;
    setJobSteps(updated);
  };

  const addCrewMember = () => {
    setFormData(prev => ({
      ...prev,
      crew_members: [...prev.crew_members, '']
    }));
  };

  const updateCrewMember = (index, value) => {
    const updated = [...formData.crew_members];
    updated[index] = value;
    setFormData(prev => ({ ...prev, crew_members: updated }));
  };

  // Handle when user finishes editing a crew member name (onBlur)
  const handleCrewMemberBlur = (index) => {
    const memberName = formData.crew_members[index]?.trim();
    const previousName = previousCrewMembers[index]?.trim();
    
    // Only proceed if there's a valid name
    if (!memberName || memberName.length === 0) {
      return;
    }
    
    // Check if name changed from previous
    const nameChanged = previousName && previousName !== memberName;
    
    if (nameChanged) {
      // Update existing signature with new name
      setWorkerSignatures(prev => {
        const updatedSignatures = prev.map(sig => {
          if (sig.name?.toLowerCase().trim() === previousName.toLowerCase()) {
            return { ...sig, name: memberName };
          }
          return sig;
        });
        return updatedSignatures;
      });
    } else {
      // Check if signature already exists for this name
      const signatureExists = workerSignatures.some(
        sig => sig.name?.toLowerCase().trim() === memberName.toLowerCase()
      );
      
      // Only add new signature if it doesn't exist
      if (!signatureExists) {
        setWorkerSignatures(prev => [...prev, {
          name: memberName,
          position: 'Worker',
          signature_data: null,
          timestamp: new Date().toISOString()
        }]);
      }
    }
    
    // Update previous crew members tracking
    setPreviousCrewMembers(prev => {
      const updated = [...prev];
      updated[index] = memberName;
      return updated;
    });
  };

  const addWorkerSignature = (name, position) => {
    setWorkerSignatures([...workerSignatures, {
      name,
      position,
      signature_data: null,
      timestamp: new Date().toISOString()
    }]);
  };

  // Compress and resize image before saving
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress to 70% quality JPEG
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle photo capture from camera
  const handlePhotoCapture = async (e, itemIndex) => {
    const file = e.target.files[0];
    if (file) {
      const compressedPhoto = await compressImage(file);
      const updated = { ...inspection };
      if (!updated.inspection_items[itemIndex].photos) {
        updated.inspection_items[itemIndex].photos = [];
      }
      updated.inspection_items[itemIndex].photos.push(compressedPhoto);
      setInspection(updated);
    }
    e.target.value = '';
  };

  // Handle photo upload (multiple files)
  const handlePhotoUpload = async (e, itemIndex) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const compressedPhotos = await Promise.all(files.map(file => compressImage(file)));
      const updated = { ...inspection };
      if (!updated.inspection_items[itemIndex].photos) {
        updated.inspection_items[itemIndex].photos = [];
      }
      updated.inspection_items[itemIndex].photos.push(...compressedPhotos);
      setInspection(updated);
    }
    e.target.value = '';
  };

  const saveInspection = async () => {
    if (locked) return;
    setSaving(true);
    
    try {
      requireToken();
      
      // Validate completion
      const incompleteItems = inspection.inspection_items.filter(item => 
        item.status === null || item.status === undefined
      );
      
      if (incompleteItems.length > 0) {
        alert(`Please complete all inspection items. ${incompleteItems.length} items still need to be reviewed.`);
        setSaving(false);
        return;
      }

      // NEW: Check if defects have required photos
      const defectsWithoutPhotos = inspection.inspection_items.filter(item => 
        item.status === 'fail' && (!item.photos || item.photos.length === 0)
      );
      
      if (defectsWithoutPhotos.length > 0) {
        alert(`❌ PHOTOS REQUIRED: All defects must have at least one photo. ${defectsWithoutPhotos.length} defect(s) still need photos.`);
        setSaving(false);
        return;
      }
      
      // Check if defects have notes
      const defectsWithoutNotes = inspection.inspection_items.filter(item => 
        (item.status === 'fail' || item.status === 'needs_attention') && (!item.notes || item.notes.trim() === '')
      );
      
      if (defectsWithoutNotes.length > 0) {
        alert(`❌ NOTES REQUIRED: All defects and issues must have detailed notes. ${defectsWithoutNotes.length} item(s) still need notes.`);
        setSaving(false);
        return;
      }

      // REQUIRED: Everyone on site must sign before the PSI can be submitted
      const crewNames = (formData.crew_members || [])
        .map(n => (n || '').trim())
        .filter(n => n.length > 0);

      if (crewNames.length === 0 && workerSignatures.length === 0) {
        alert('❌ SIGNATURES REQUIRED: Add the crew on site and have everyone sign before submitting.');
        setSaving(false);
        return;
      }

      const unsignedCrew = [];
      // Every named crew member must have a signed signature
      crewNames.forEach(name => {
        const sig = workerSignatures.find(
          s => s.name && s.name.toLowerCase().trim() === name.toLowerCase()
        );
        if (!sig || !sig.signature_data) unsignedCrew.push(name);
      });
      // Any visitor/subcontractor entries must also be signed
      workerSignatures.forEach(s => {
        const name = (s.name || '').trim();
        if (name && !s.signature_data && !unsignedCrew.some(n => n.toLowerCase() === name.toLowerCase())) {
          unsignedCrew.push(name);
        }
      });

      if (unsignedCrew.length > 0) {
        alert(`❌ SIGNATURES REQUIRED\n\nEveryone on site must sign before this PSI can be submitted.\n\nStill needs to sign:\n• ${unsignedCrew.join('\n• ')}`);
        setSaving(false);
        return;
      }

      const nowET = new Date();
      const inspectionData = {
        ...inspection,
        ...formData,
        inspection_date: inspection?.inspection_date || todayET,
        inspection_time: inspection?.inspection_time || nowET.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Toronto' }),
        hazard_assessments: hazardAssessments,
        job_steps: jobSteps,
        worker_signatures: workerSignatures,
        overall_risk_score: calculateOverallRisk()
      };

      const isEditing = !!inspectionId;
      // Goes straight to Ops when there's signal; otherwise it's saved on this phone and uploaded later.
      const sent = await sendOrQueue('psi_submit',
        `PSI ${inspectionData.inspection_date} · WO ${formData.work_order_number || '—'}${formData.job_site ? ` · ${formData.job_site}` : ''}`,
        { inspectionId: isEditing ? inspectionId : null, inspection: inspectionData });
      setPsiQueued(sent.queued);

      {
        // Clear draft on successful save
        clearDraft();
        
        // If there are worker signatures, offer to clock them in
        // (safe on edits too — anyone already clocked in is skipped)
        if (workerSignatures.length > 0) {
          setSavedWO(formData.work_order_number || '');
          setShowCrewClockin(true);
        } else if (sent.queued) {
          alert('No signal — the PSI is saved on this phone and will upload on its own when you are back in service.');
          navigate('/mobile/inspections', { state: { refresh: true } });
        } else {
          alert(isEditing ? 'Daily PSI updated successfully!' : 'Daily PSI completed successfully!');
          navigate('/mobile/inspections', { state: { refresh: true } });
        }
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert(error.response?.data?.detail || error.message || 'Failed to save inspection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClockInCrew = async () => {
    setCrewClockLoading(true);
    try {
      requireToken();
      const crewNames = workerSignatures
        .map(sig => sig.name)
        .filter(name => name && name.trim());
      
      // Get foreman's GPS location
      let location = null;
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      } catch (gpsErr) {
        console.warn('GPS unavailable:', gpsErr.message);
      }
      
      // clock_date pins the day the PSI was actually done, so a no-signal upload tomorrow still lands today
      const sent = await sendOrQueue('crew_clock_in',
        `Clock in ${crewNames.length} on WO ${savedWO || '—'} at ${crewClockTime} (${todayET})`,
        { crew_names: crewNames, clock_time: crewClockTime, clock_date: todayET, work_order_number: savedWO, shift: 'days', location });
      if (sent.queued) {
        setCrewClockResults({ queued: true, clocked_in: 0, contractors: 0,
          results: crewNames.map(n => ({ name: n, status: 'queued' })) });
      } else {
        setCrewClockResults(sent.data);
      }
    } catch (error) {
      console.error('Clock-in failed:', error);
      if (error.response?.status === 401) {
        // Token died between the PSI save and the clock-in (expired / sign-in no longer valid). The PSI is saved.
        alert('Your sign-in has expired, so the crew could not be clocked in. The PSI itself is saved.\n\nSign in again and clock the crew in from the Field Tracker.');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      alert(`Clock-in failed: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setCrewClockLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Daily PSI...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="flex items-center">
          <button onClick={() => {
            // Save draft before navigating away
            if (!inspectionId && inspection) {
              try {
                localStorage.setItem(draftKey, JSON.stringify({
                  formData, inspection, hazardAssessments, jobSteps,
                  workerSignatures: workerSignatures.map(s => ({ ...s, signature_data: s.signature_data || null })),
                  savedAt: new Date().toISOString()
                }));
              } catch (e) { /* ignore */ }
            }
            navigate('/mobile/inspections');
          }} className="mr-4">
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Daily PSI</h1>
            <p className="text-blue-800 text-sm">Pre-Job Safety Instructions</p>
          </div>
          {hasDraft && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-blue-800 bg-blue-100 px-2 py-1 rounded">
                Draft {lastSaved ? lastSaved.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'saved'}
              </span>
              <button onClick={() => { clearDraft(); window.location.reload(); }}
                className="text-[10px] text-blue-800 underline hover:text-slate-900"
                data-testid="clear-draft-btn">
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {locked && (
        <div className="bg-amber-50 border-b border-amber-300 p-4" data-testid="psi-locked-banner">
          <div className="flex items-start gap-2">
            <NoSymbolIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 font-medium">
              This PSI is locked. It can be edited by the foreman who created it (on the same day) or a manager/admin. You can view it but not edit.
            </p>
          </div>
        </div>
      )}

      <fieldset disabled={locked} className="contents">

      {/* Overall Risk Score */}
      <div className={`p-4 border-b ${getRiskColor(calculateOverallRisk())}`}>
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg">Overall Risk Score</h3>
            <p className="text-sm">Combined risk assessment for today's work</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{calculateOverallRisk()}</div>
            <div className="text-sm">
              {calculateOverallRisk() <= 8 ? 'LOW RISK' :
               calculateOverallRisk() <= 16 ? 'MEDIUM RISK' :
               calculateOverallRisk() <= 24 ? 'HIGH RISK' : 'VERY HIGH RISK'}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Same site as yesterday? Offer the foreman's last PSI as a starting point */}
        {!inspectionId && !templateUsed && !templateDismissed && recentPSIs.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4 shadow-sm" data-testid="psi-template-banner">
            <h2 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
              <DocumentDuplicateIcon className="h-4 w-4" /> Same site, same job as last time?
            </h2>
            <p className="text-xs text-emerald-800 mt-1">Start from one of your recent PSIs — WO, site, job description, crew, hazards and tasks are copied. Then just add or remove people and tasks. Today's checklist, weather and signatures start fresh.</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <select value={templatePick} onChange={(e) => setTemplatePick(e.target.value)} data-testid="psi-template-select"
                className="flex-1 p-3 border border-emerald-300 rounded-lg text-gray-900 bg-white text-sm">
                {recentPSIs.map((p) => <option key={p.inspection_id} value={p.inspection_id}>{psiLabel(p)}</option>)}
              </select>
              <button type="button" onClick={() => applyTemplate(recentPSIs.find((p) => p.inspection_id === templatePick) || recentPSIs[0])}
                className="px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold" data-testid="psi-template-use-btn">
                Use as today's starting point
              </button>
              <button type="button" onClick={() => setTemplateDismissed(true)}
                className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm" data-testid="psi-template-dismiss-btn">
                Start blank
              </button>
            </div>
          </div>
        )}
        {!inspectionId && templateUsed && (
          <div className="bg-emerald-100 border border-emerald-300 rounded-lg px-4 py-2 text-xs text-emerald-900 flex items-center justify-between gap-2" data-testid="psi-template-applied">
            <span>✅ Copied from your {fmtPsiDay(templateUsed.inspection_date)} PSI (WO {templateUsed.work_order_number || '—'}). Check every hazard and task still applies — everyone on site signs again today.</span>
            <button type="button" onClick={() => { setTemplateUsed(null); setTemplateDismissed(false); }} className="underline whitespace-nowrap" data-testid="psi-template-change-btn">Pick another</button>
          </div>
        )}

        {/* Edit History — shows when this PSI was created and each mid-day change */}
        {inspectionId && inspection && (
          <div className="bg-white rounded-lg p-4 shadow-sm" data-testid="psi-history-strip">
            <h2 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <ClockIcon className="h-4 w-4 text-gray-500" />
              Edit History
            </h2>
            <ul className="space-y-1 text-xs text-gray-600">
              <li className="flex gap-1.5" data-testid="psi-history-created">
                <span className="text-green-600 font-medium">Created</span>
                <span>
                  {fmtHistoryTs(inspection.created_at) || inspection.inspection_time || ''}
                  {inspection.inspector_name ? ` by ${inspection.inspector_name}` : ''}
                </span>
              </li>
              {(inspection.edit_history || []).map((h, i) => (
                <li key={i} className="flex gap-1.5" data-testid={`psi-history-edit-${i}`}>
                  <span className="text-blue-600 font-medium">Updated</span>
                  <span>
                    {fmtHistoryTs(h.at)}{h.by_name ? ` by ${h.by_name}` : ''}
                    {h.changes && h.changes.length > 0 ? ` — ${h.changes.join(', ')}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Basic Information */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work Order Number</label>
              <input
                type="text"
                value={formData.work_order_number}
                onChange={(e) => setFormData(prev => ({ ...prev, work_order_number: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg text-gray-900"
                placeholder="Enter work order number (e.g., 25-1002)"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Job Site</label>
              <input
                type="text"
                value={formData.job_site}
                onChange={(e) => setFormData(prev => ({ ...prev, job_site: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg text-gray-900"
                placeholder="Enter job site location"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work Description</label>
              <textarea
                value={formData.work_description}
                onChange={(e) => setFormData(prev => ({ ...prev, work_description: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg text-gray-900"
                rows="3"
                placeholder="Describe today's work activities"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Weather</label>
                <select
                  value={formData.weather_conditions}
                  onChange={(e) => setFormData(prev => ({ ...prev, weather_conditions: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg text-gray-900"
                >
                  <option value="">Select...</option>
                  <option value="clear">Clear</option>
                  <option value="cloudy">Cloudy</option>
                  <option value="rain">Rain</option>
                  <option value="snow">Snow</option>
                  <option value="fog">Fog</option>
                  <option value="windy">Windy</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Temperature</label>
                <input
                  type="text"
                  value={formData.temperature}
                  onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg text-gray-900"
                  placeholder="°C"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Crew Members */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Crew Members</h2>
          
          {formData.crew_members.map((member, index) => {
            const parts = (member || '').split(' ');
            const firstName = parts[0] || '';
            const lastName = parts.slice(1).join(' ') || '';
            return (
              <div key={index} className="flex items-center gap-2 mb-2">
                <div className="flex-1 flex gap-2 min-w-0">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      const newFirst = e.target.value;
                      const combined = `${newFirst} ${lastName}`.trim();
                      updateCrewMember(index, combined);
                    }}
                    onBlur={() => handleCrewMemberBlur(index)}
                    className="w-1/2 min-w-0 p-2 border border-gray-300 rounded"
                    placeholder="First"
                    data-testid={`crew-first-name-${index}`}
                  />
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => {
                      const newLast = e.target.value;
                      const combined = `${firstName} ${newLast}`.trim();
                      updateCrewMember(index, combined);
                    }}
                    onBlur={() => handleCrewMemberBlur(index)}
                    className="w-1/2 min-w-0 p-2 border border-gray-300 rounded"
                    placeholder="Last"
                    data-testid={`crew-last-name-${index}`}
                  />
                </div>
                {index > 0 && (
                  <button
                    onClick={() => {
                      const removedName = (formData.crew_members[index] || '').trim().toLowerCase();
                      const updated = formData.crew_members.filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, crew_members: updated }));
                      // Remove the matching crew signature so a removed person can't block submission
                      if (removedName) {
                        setWorkerSignatures(prev => prev.filter(
                          s => (s.name || '').toLowerCase().trim() !== removedName
                        ));
                      }
                    }}
                    className="text-red-500 flex-shrink-0"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            );
          })}
          
          <button
            onClick={addCrewMember}
            className="flex items-center text-blue-600 text-sm mt-2"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Crew Member
          </button>
        </div>

        {/* Hazard Risk Assessment */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Hazard Risk Assessment</h2>
            <button
              onClick={addHazardAssessment}
              className="flex items-center text-blue-600 text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Hazard
            </button>
          </div>
          
          {hazardAssessments.map((hazard, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-start mb-3">
                <input
                  type="text"
                  value={hazard.hazard_type}
                  onChange={(e) => updateHazardAssessment(index, 'hazard_type', e.target.value)}
                  className="font-medium text-gray-800 bg-transparent border-b border-gray-300 focus:border-blue-500"
                  placeholder="Hazard type"
                />
                {index >= 4 && (
                  <button
                    onClick={() => removeHazardAssessment(index)}
                    className="text-red-500 ml-2"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
              
              <textarea
                value={hazard.description}
                onChange={(e) => updateHazardAssessment(index, 'description', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 mb-3"
                rows="2"
                placeholder="Describe the specific hazards..."
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                  <select
                    value={hazard.risk_level}
                    onChange={(e) => updateHazardAssessment(index, 'risk_level', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded text-gray-900"
                  >
                    {riskLevels.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.value} - {level.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className={`flex items-center justify-center rounded ${getRiskColor(hazard.risk_level)}`}>
                  <span className="font-bold text-2xl">{hazard.risk_level}</span>
                </div>
              </div>
              
              <textarea
                value={hazard.control_measures}
                onChange={(e) => updateHazardAssessment(index, 'control_measures', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 mt-3"
                rows="2"
                placeholder="Control measures and safety procedures..."
              />
            </div>
          ))}
        </div>

        {/* Job Steps Analysis */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Job Steps & Task Analysis</h2>
            <span className="text-xs text-gray-500">{jobSteps.length} step{jobSteps.length !== 1 ? 's' : ''}</span>
          </div>
          
          {jobSteps.map((step, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-800">Step {step.step_number}</h3>
                {jobSteps.length > 1 && (
                  <button
                    onClick={() => removeJobStep(index)}
                    className="text-red-500"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
              
              <textarea
                value={step.description}
                onChange={(e) => updateJobStep(index, 'description', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 mb-3"
                rows="2"
                placeholder="Describe this job step..."
              />
              
              <textarea
                value={step.hazards}
                onChange={(e) => updateJobStep(index, 'hazards', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 mb-3"
                rows="2"
                placeholder="Hazards associated with this step..."
              />
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                  <select
                    value={step.risk_level}
                    onChange={(e) => updateJobStep(index, 'risk_level', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded text-gray-900"
                  >
                    {riskLevels.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.value} - {level.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className={`flex items-center justify-center rounded ${getRiskColor(step.risk_level)}`}>
                  <span className="font-bold text-2xl">{step.risk_level}</span>
                </div>
              </div>
              
              <textarea
                value={step.control_measures}
                onChange={(e) => updateJobStep(index, 'control_measures', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded text-gray-900"
                rows="2"
                placeholder="Control measures for this step..."
              />
            </div>
          ))}

          <button
            onClick={addJobStep}
            data-testid="add-step-btn"
            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors font-medium"
          >
            <PlusIcon className="h-5 w-5" />
            Add Step
          </button>
        </div>

        {/* Digital Signatures */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Crew Signatures</h2>
            <p className="text-xs text-red-600 font-semibold">Required — everyone on site must sign to submit</p>
          </div>
          
          {workerSignatures.map((signature, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-3 mb-3">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div>
                      <p className="font-medium">{signature.name}</p>
                      <p className="text-sm text-gray-600">{signature.position}</p>
                      {signature.signature_data && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Signed at {new Date(signature.timestamp).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'America/Toronto'
                          })}
                        </p>
                      )}
                    </div>
                    
                    {/* Signature Preview */}
                    {signature.signature_data && (
                      <div className="flex-shrink-0">
                        <div className="w-20 h-12 border border-gray-300 rounded bg-white overflow-hidden">
                          <img
                            src={signature.signature_data}
                            alt={`${signature.name} signature`}
                            className="w-full h-full object-contain"
                            style={{ imageRendering: 'crisp-edges' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right ml-3">
                  {signature.signature_data ? (
                    <button
                      onClick={() => {
                        setCurrentSignature(index);
                        setShowSignatureModal(true);
                      }}
                      className="text-blue-600 text-sm hover:text-blue-800"
                    >
                      Re-sign
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setCurrentSignature(index);
                        setShowSignatureModal(true);
                      }}
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Sign Here
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          <div className="mt-4 p-4 border border-dashed border-gray-300 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Add Visitors or Subcontractors</h4>
            <div className="flex space-x-4">
              <input
                type="text"
                placeholder="Visitor/contractor name"
                className="flex-1 p-2 border border-gray-300 rounded"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    const name = e.target.value.trim();
                    const position = e.target.nextElementSibling.value.trim() || 'Visitor';
                    addWorkerSignature(name, position);
                    e.target.value = '';
                    e.target.nextElementSibling.value = '';
                  }
                }}
              />
              <input
                type="text"
                placeholder="Company/Position"
                className="flex-1 p-2 border border-gray-300 rounded"
              />
              <button
                onClick={(e) => {
                  const nameInput = e.target.parentNode.children[0];
                  const positionInput = e.target.parentNode.children[1];
                  if (nameInput.value.trim()) {
                    addWorkerSignature(nameInput.value.trim(), positionInput.value.trim() || 'Visitor');
                    nameInput.value = '';
                    positionInput.value = '';
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Add Visitor
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              For visitors, subcontractors, or anyone not in the crew list above
            </p>
          </div>
        </div>

        {/* Safety Checklist (Enhanced Layout) */}
        {inspection && (
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Equipment & Safety Inspection</h2>
            <p className="text-sm text-gray-600 mb-4">Complete each item before proceeding. Photos required for defects.</p>
            
            <div className="space-y-4">
              {inspection.inspection_items.map((item, index) => (
                <div key={item.id} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                  {/* Item Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="mt-1">
                        <input
                          type="checkbox"
                          checked={item.status !== null && item.status !== undefined}
                          onChange={() => {}}
                          className="w-5 h-5 text-blue-600 rounded cursor-pointer"
                          disabled
                        />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.description}</p>
                        {(item.status === null || item.status === undefined) && (
                          <span className="inline-block mt-1 text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-full font-medium">
                            ⚠️ Action Required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status Selection - Horizontal Cards */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <button
                      onClick={() => {
                        const updated = { ...inspection };
                        updated.inspection_items[index].status = 'pass';
                        setInspection(updated);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        item.status === 'pass'
                          ? 'bg-green-50 border-green-500 shadow-md'
                          : 'bg-white border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <CheckCircleIcon className={`h-6 w-6 mb-1 ${
                          item.status === 'pass' ? 'text-green-600' : 'text-slate-500'
                        }`} />
                        <span className={`text-sm font-medium ${
                          item.status === 'pass' ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          Pass
                        </span>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        const updated = { ...inspection };
                        updated.inspection_items[index].status = 'needs_attention';
                        setInspection(updated);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        item.status === 'needs_attention'
                          ? 'bg-yellow-50 border-yellow-500 shadow-md'
                          : 'bg-white border-gray-200 hover:border-yellow-300'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <ExclamationTriangleIcon className={`h-6 w-6 mb-1 ${
                          item.status === 'needs_attention' ? 'text-yellow-600' : 'text-slate-500'
                        }`} />
                        <span className={`text-sm font-medium ${
                          item.status === 'needs_attention' ? 'text-yellow-700' : 'text-gray-600'
                        }`}>
                          Minor Issue
                        </span>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        const updated = { ...inspection };
                        updated.inspection_items[index].status = 'fail';
                        setInspection(updated);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        item.status === 'fail'
                          ? 'bg-red-50 border-red-500 shadow-md'
                          : 'bg-white border-gray-200 hover:border-red-300'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <XCircleIcon className={`h-6 w-6 mb-1 ${
                          item.status === 'fail' ? 'text-red-600' : 'text-slate-500'
                        }`} />
                        <span className={`text-sm font-medium ${
                          item.status === 'fail' ? 'text-red-700' : 'text-gray-600'
                        }`}>
                          Defect
                        </span>
                      </div>
                    </button>
                  </div>

                  {/* Notes and Photos for Issues */}
                  {(item.status === 'fail' || item.status === 'needs_attention') && (
                    <div className="mt-3 pt-3 border-t border-gray-300 space-y-3">
                      <textarea
                        value={item.notes || ''}
                        onChange={(e) => {
                          const updated = { ...inspection };
                          updated.inspection_items[index].notes = e.target.value;
                          setInspection(updated);
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg text-gray-900 text-sm"
                        rows="3"
                        placeholder="Describe the issue and corrective actions taken..."
                      />
                      
                      {/* Photo Upload Section */}
                      <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          📸 Photo Documentation {item.status === 'fail' && <span className="text-red-600">(Required)</span>}
                        </label>
                        
                        <div className="flex flex-wrap gap-2 mb-2">
                          {item.photos && item.photos.length > 0 && item.photos.map((photo, photoIndex) => (
                            <div key={photoIndex} className="relative">
                              <img 
                                src={photo} 
                                alt={`Defect ${photoIndex + 1}`}
                                className="w-20 h-20 object-cover rounded border border-gray-300"
                              />
                              <button
                                onClick={() => {
                                  const updated = { ...inspection };
                                  updated.inspection_items[index].photos = item.photos.filter((_, i) => i !== photoIndex);
                                  setInspection(updated);
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <label className="flex-1 cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={(e) => handlePhotoCapture(e, index)}
                              className="hidden"
                            />
                            <div className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                              📷 Take Photo
                            </div>
                          </label>
                          
                          <label className="flex-1 cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => handlePhotoUpload(e, index)}
                              className="hidden"
                            />
                            <div className="flex items-center justify-center px-4 py-2 bg-slate-200 text-slate-900 rounded-lg hover:bg-slate-100 text-sm">
                              🖼️ Upload Photo
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Save Button */}
        <div className="sticky bottom-0 p-4 bg-white border-t border-gray-200 shadow-lg mt-6">
          {locked ? (
            <div className="w-full bg-amber-100 border border-amber-300 text-amber-800 font-semibold py-4 px-6 rounded-lg shadow-lg flex items-center justify-center" data-testid="psi-locked-save">
              <NoSymbolIcon className="h-6 w-6 mr-2" />
              Locked — manager approval required to edit
            </div>
          ) : (
            <button
              onClick={saveInspection}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg shadow-lg flex items-center justify-center disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Saving PSI...
                </>
              ) : (
                <>
                  <ClipboardDocumentCheckIcon className="h-6 w-6 mr-2" />
                  Complete Daily PSI
                </>
              )}
            </button>
          )}
        </div>
      </div>
      </fieldset>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Digital Signature</h3>
              <p className="text-sm text-gray-600">
                {currentSignature !== null ? workerSignatures[currentSignature]?.name : ''} - {currentSignature !== null ? workerSignatures[currentSignature]?.position : ''}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Sign with your finger on the canvas below
              </p>
            </div>
            
            <div className="p-6">
              <div className="border-2 border-gray-300 rounded-lg bg-gray-50 p-2">
                <SignatureCanvas
                  ref={signatureRef}
                  penColor="black"
                  backgroundColor="white"
                  canvasProps={{
                    width: 400,
                    height: 200,
                    className: 'signature-canvas border rounded'
                  }}
                />
              </div>
              
              <p className="text-xs text-gray-500 mt-2 text-center">
                Draw your signature above. Use your finger on mobile devices.
              </p>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => signatureRef.current?.clear()}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Clear
                </button>
                <button
                  onClick={() => {
                    setShowSignatureModal(false);
                    setCurrentSignature(null);
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (currentSignature !== null && signatureRef.current) {
                      const signatureData = signatureRef.current.toDataURL('image/png', 0.8);
                      const updated = [...workerSignatures];
                      updated[currentSignature].signature_data = signatureData;
                      updated[currentSignature].timestamp = new Date().toISOString();
                      setWorkerSignatures(updated);
                      setShowSignatureModal(false);
                      setCurrentSignature(null);
                    }
                  }}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  Save Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Crew Clock-In Popup */}
      {showCrewClockin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {!crewClockResults ? (
              <>
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">Clock In Crew?</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {psiQueued ? 'No signal — the PSI is saved on this phone and uploads when you are back in service.' : 'PSI saved successfully.'} Clock in {workerSignatures.length} worker{workerSignatures.length !== 1 ? 's' : ''} to the Field Tracker?
                  </p>
                </div>
                
                <div className="p-6 space-y-4">
                  {/* Shift — drives the default start time so afternoon/night crews aren't logged at 7am */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
                    <select
                      value={crewShift}
                      onChange={(e) => {
                        const s = e.target.value;
                        setCrewShift(s);
                        setCrewClockTime({ days: '07:00', afternoons: '15:30', nights: '23:00' }[s] || '07:00');
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg text-lg bg-white"
                      data-testid="crew-clock-shift-select"
                    >
                      <option value="days">Days (7:00 AM)</option>
                      <option value="afternoons">Afternoons (3:30 PM)</option>
                      <option value="nights">Nights (11:00 PM)</option>
                    </select>
                  </div>

                  {/* Start Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={crewClockTime}
                      onChange={(e) => setCrewClockTime(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg text-lg"
                      data-testid="crew-clock-time-input"
                    />
                  </div>
                  
                  {/* Work Order */}
                  {savedWO && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">Work Order:</span> {savedWO}
                      </p>
                    </div>
                  )}
                  
                  {/* Worker Names */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Crew Members</label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {workerSignatures.map((sig, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-800">{sig.name}</span>
                          <span className="text-xs text-gray-500">({sig.position})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="p-6 border-t border-gray-200 flex gap-3">
                  <button
                    onClick={() => {
                      setShowCrewClockin(false);
                      navigate('/mobile/inspections', { state: { refresh: true } });
                    }}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                    data-testid="crew-clock-skip-btn"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleClockInCrew}
                    disabled={crewClockLoading}
                    className="flex-1 py-3 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
                    data-testid="crew-clock-submit-btn"
                  >
                    {crewClockLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Clocking In...
                      </>
                    ) : (
                      'Clock In Crew'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">{crewClockResults.queued ? 'Saved on this phone' : 'Clock-In Results'}</h2>
                  <p className="text-sm text-gray-600 mt-1" data-testid="crew-clockin-summary">
                    {crewClockResults.queued
                      ? `No signal — ${crewClockResults.results.length} crew will be clocked in at ${crewClockTime} as soon as this phone is back in service${psiQueued ? ' (the PSI uploads then too)' : ''}.`
                      : <>{crewClockResults.clocked_in} clocked in{crewClockResults.contractors > 0 ? `, ${crewClockResults.contractors} contractor${crewClockResults.contractors !== 1 ? 's' : ''} logged` : ''}</>}
                  </p>
                </div>
                
                <div className="p-6 space-y-2 max-h-60 overflow-y-auto">
                  {crewClockResults.results.map((r, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg ${
                      r.status === 'clocked_in' ? 'bg-green-50' : 
                      r.status === 'already_in' || r.status === 'already_done' || r.status === 'queued' ? 'bg-yellow-50' :
                      r.status === 'contractor' ? 'bg-blue-50' : 'bg-red-50'
                    }`}>
                      {r.status === 'clocked_in' ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : r.status === 'already_in' || r.status === 'already_done' || r.status === 'queued' ? (
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                      ) : r.status === 'contractor' ? (
                        <DocumentTextIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-800">{r.name}</span>
                        {r.matched && r.matched !== r.name && (
                          <span className="text-xs text-gray-500 ml-1">→ {r.matched}</span>
                        )}
                        {r.status === 'already_done' && r.reason && (
                          <p className="text-[11px] text-yellow-700" data-testid="crew-already-done-reason">{r.reason}</p>
                        )}
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                        r.status === 'clocked_in' ? 'bg-green-100 text-green-700' :
                        r.status === 'already_in' || r.status === 'already_done' || r.status === 'queued' ? 'bg-yellow-100 text-yellow-700' :
                        r.status === 'contractor' ? 'bg-blue-100 text-blue-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {r.status === 'clocked_in' ? 'Clocked In' :
                         r.status === 'queued' ? 'Waiting for signal' :
                         r.status === 'already_in' ? 'Already In' :
                         r.status === 'already_done' ? 'Already Worked Today' :
                         r.status === 'contractor' ? 'Contractor' : 'No Match'}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="p-6 border-t border-gray-200 space-y-2">
                  <button
                    onClick={() => {
                      setShowCrewClockin(false);
                      setCrewClockResults(null);
                      navigate('/mobile/field-crew');
                    }}
                    className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    data-testid="crew-clock-done-btn"
                  >
                    View Crew on Site
                  </button>
                  <button
                    onClick={() => {
                      setShowCrewClockin(false);
                      setCrewClockResults(null);
                      navigate('/mobile/inspections', { state: { refresh: true } });
                    }}
                    className="w-full py-2 px-4 text-gray-600 text-sm hover:text-gray-800"
                  >
                    Back to Inspections
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyPSIForm;